import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useQueries, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiPlus, mdiRefresh, mdiSigma } from '@mdi/js'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Input } from '../ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { apiClient } from '../../api/client'
import { apiErrorMessage } from '../../lib/apiError'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useLocale } from '../../contexts/LocaleContext'
import { Can } from '../../contexts/PermissionsContext'
import { relationPhrase, relationTypeLabel, type AssetRelationType } from './relation-type-form'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { EmptyState } from '../ui/empty-state'
import { mdiVectorLink } from '@mdi/js'

/** Shared query key so the create/detail pages can bust this list. */
export const relationTypesQueryKey = (org: string) => ['relationTypes', org]

export async function fetchRelationTypes(org: string): Promise<AssetRelationType[]> {
    const response = await apiClient.GET('/api/v1/organizations/{orgCode}/asset-relation-types', {
        params: { path: { orgCode: org } },
    })
    if (response.error) throw new Error(apiErrorMessage(response.error, 'Failed to load relation types'))
    return ((response.data as { data?: AssetRelationType[] })?.data || []) as AssetRelationType[]
}

/**
 * Usage count for one relation type. `GET /asset-relations` is paginated and
 * returns `total`, so a `limit=1` probe is the cheapest way to get the count
 * without a dedicated endpoint (there are only a handful of relation types per
 * org, so the fan-out stays small).
 */
export async function fetchRelationUsage(org: string, code: string): Promise<number> {
    const response = await apiClient.GET('/api/v1/organizations/{orgCode}/asset-relations', {
        params: { path: { orgCode: org }, query: { relation_type: code, limit: 1 } },
    })
    if (response.error) throw new Error(apiErrorMessage(response.error, 'Failed to count relations'))
    return (response.data as { total?: number })?.total ?? 0
}

export const relationUsageQueryKey = (org: string, code: string) => ['relationTypeUsage', org, code]

export function RelationTypesPage() {
    const { org } = useParams({ strict: false }) as { org?: string }
    const orgCode = org || 'demo'
    const { t } = useTranslation('types')
    const { locale } = useLocale()
    const navigate = useNavigate()
    const [searchQuery, setSearchQuery] = useState('')

    const {
        data: relationTypes = [],
        isLoading,
        error,
        refetch,
    } = useQuery({
        queryKey: relationTypesQueryKey(orgCode),
        queryFn: () => fetchRelationTypes(orgCode),
    })

    useBreadcrumbs(
        useMemo(
            () => [
                { title: t('breadcrumb.home'), path: `/${orgCode}` },
                { title: t('breadcrumb.org_settings'), path: `/${orgCode}/settings` },
                { title: t('relation_types.title') },
            ],
            [orgCode, t]
        )
    )

    const usageQueries = useQueries({
        queries: relationTypes.map(rt => ({
            queryKey: relationUsageQueryKey(orgCode, rt.code),
            queryFn: () => fetchRelationUsage(orgCode, rt.code),
        })),
    })

    const usageByCode = useMemo(() => {
        const m = new Map<string, number | undefined>()
        relationTypes.forEach((rt, i) => m.set(rt.code, usageQueries[i]?.data))
        return m
    }, [relationTypes, usageQueries])

    // The API already orders by `ordering ASC NULLS LAST, code ASC`; the sort
    // below is defensive so the UI order never depends on the transport.
    const filtered = useMemo(() => {
        const q = searchQuery.trim().toLowerCase()
        const rows = q
            ? relationTypes.filter(
                  rt =>
                      rt.code.toLowerCase().includes(q) ||
                      relationTypeLabel(rt, locale).toLowerCase().includes(q)
              )
            : relationTypes
        return [...rows].sort((a, b) => {
            const ao = a.ordering ?? Number.MAX_SAFE_INTEGER
            const bo = b.ordering ?? Number.MAX_SAFE_INTEGER
            if (ao !== bo) return ao - bo
            return a.code.localeCompare(b.code)
        })
    }, [relationTypes, searchQuery, locale])

    if (error) {
        return (
            <div className="p-6 text-center">
                <p className="text-destructive">
                    {t('relation_types.error_loading', { message: (error as Error).message })}
                </p>
                <Button onClick={() => void refetch()} className="mt-4" data-testid="relation-types-retry">
                    <AppIcon path={mdiRefresh} size="md" className="mr-2" />
                    {t('relation_types.retry_button')}
                </Button>
            </div>
        )
    }

    return (
        <div className="container mx-auto max-w-6xl py-8">
            <div className="rounded-lg bg-card shadow-lg">
                <div className="border-b border-border px-6 py-4">
                    <PageHeader
                        icon={SECTION_ICONS.relationTypes}
                        title={t('relation_types.title')}
                        description={t('relation_types.subtitle')}
                        actions={
                        <>
                        <Button
                            variant="outline"
                            onClick={() => void refetch()}
                            aria-label={t('relation_types.refresh_button')}
                            title={t('relation_types.refresh_button')}
                            data-testid="relation-types-refresh-btn"
                        >
                            <AppIcon path={mdiRefresh} size="sm" className="md:mr-1" />
                            <span className="hidden md:inline">{t('relation_types.refresh_button')}</span>
                        </Button>
                        {/* Creating a relation type is `configuration.write`; a
                            read-only caller keeps the list and the detail
                            navigation (spec 2026-09-04-02, "Gating"). */}
                        <Can perm="configuration.write">
                            <Link to="/$org/relation-types/create" params={{ org: orgCode }}>
                                <Button
                                    aria-label={t('relation_types.create')}
                                    title={t('relation_types.create')}
                                    data-testid="relation-type-create-btn"
                                >
                                    <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
                                    <span className="hidden md:inline">{t('relation_types.create')}</span>
                                </Button>
                            </Link>
                        </Can>
                        </>
                        }
                    />
                </div>

                <div className="p-6">
                    <Input
                        placeholder={t('relation_types.search_placeholder')}
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="mb-4 max-w-sm"
                        data-testid="relation-types-search"
                    />

                    {isLoading ? (
                        <div className="py-8 text-center text-muted-foreground" data-testid="relation-types-loading">
                            {t('relation_types.loading')}
                        </div>
                    ) : filtered.length === 0 ? (
                        <EmptyState
                            variant="inline"
                            live
                            icon={mdiVectorLink}
                            title={
                                relationTypes.length === 0
                                    ? t('relation_types.empty')
                                    : t('relation_types.no_match')
                            }
                            data-testid="relation-types-empty"
                        />
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('relation_types.column_type')}</TableHead>
                                    <TableHead>{t('relation_types.column_code')}</TableHead>
                                    <TableHead>{t('relation_types.column_phrase')}</TableHead>
                                    <TableHead>{t('relation_types.column_cardinality')}</TableHead>
                                    <TableHead>{t('relation_types.column_usage')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.map(rt => {
                                    const phrase = relationPhrase(rt, locale)
                                    const usage = usageByCode.get(rt.code)
                                    return (
                                        <TableRow
                                            key={rt.uid}
                                            className="cursor-pointer"
                                            onClick={() =>
                                                navigate({
                                                    to: '/$org/relation-types/$relationTypeUid',
                                                    params: { org: orgCode, relationTypeUid: rt.uid },
                                                })
                                            }
                                            data-testid={`relation-type-row-${rt.code}`}
                                        >
                                            <TableCell className="font-medium">
                                                <div className="flex items-center gap-2">
                                                    {rt.emoji && <span aria-hidden="true">{rt.emoji}</span>}
                                                    {rt.color && (
                                                        <span
                                                            className="inline-block h-3 w-3 shrink-0 rounded-full border border-border"
                                                            style={{ backgroundColor: rt.color }}
                                                            data-testid={`relation-type-color-${rt.code}`}
                                                        />
                                                    )}
                                                    <span>{relationTypeLabel(rt, locale)}</span>
                                                    {rt.aggregating && (
                                                        <Badge
                                                            variant="secondary"
                                                            title={t('relation_types.aggregating_tooltip')}
                                                            data-testid={`relation-type-aggregating-${rt.code}`}
                                                        >
                                                            <AppIcon path={mdiSigma} size="xs" className="mr-1" />
                                                            {t('relation_types.aggregating_badge')}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <code className="rounded bg-muted px-2 py-1 text-xs">{rt.code}</code>
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {phrase || '—'}
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {t('relation_types.cardinality.summary', {
                                                    source: t(`relation_types.cardinality.${rt.source_cardinality}`),
                                                    target: t(`relation_types.cardinality.${rt.target_cardinality}`),
                                                })}
                                            </TableCell>
                                            <TableCell
                                                className="text-sm"
                                                data-testid={`relation-type-usage-${rt.code}`}
                                            >
                                                {usage === undefined
                                                    ? t('relation_types.usage_unknown')
                                                    : t('relation_types.usage_count', { count: usage })}
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </div>
        </div>
    )
}
